/**
 * Order visibility SSOT — Recent Orders vs global agents dashboard.
 * Keep aligned with HotelsRiver_BE-main_whist/services/order_visibility_ssot.py
 */

import { resolveHomeFeedViewerIsAdmin } from "../../../utils/recentOrdersHomeFeed";
import {
  deriveLdsOrderBusinessStatus,
  isKnownBusinessStatus,
  LDS_ORDER_BUSINESS_STATUS,
  shouldIncludeLdsOrderInOrdersFeed,
} from "../../../utils/ldsOrderStatus";

const HOTEL_PENDING_BOOKING = "PAYMENT_CAPTURED_PENDING_BOOKING";
const CONFIRMED_HOTEL_STATUSES = new Set([
  "OK",
  "CONFIRMED",
  "RESERVED",
  "BOOKED",
]);

export const ORDER_VISIBILITY_SSOT_TRACE_TAG = "[ORDER_VISIBILITY_SSOT_TRACE]";

function isTrueLike(value) {
  return value === true || value === 1 || String(value).trim().toLowerCase() === "true";
}

function productType(order) {
  const t = String(order?.type || "").trim().toLowerCase();
  if (t) return t;
  if (order?.hotelorderid != null || order?.hotelname != null) return "hotel";
  if (order?.order_response != null || order?.dealid != null) return "package";
  return "unknown";
}

function evaluateHotelRecentEligibility(order) {
  if (isTrueLike(order?.cancelled)) {
    return { include: false, reason: "cancelled" };
  }
  if (isTrueLike(order?.is_offer)) {
    return { include: false, reason: "is_offer" };
  }
  let sumpaid = 0;
  try {
    sumpaid = Number(order?.sumpaid ?? order?.elig_hp_sumpaid ?? 0);
  } catch {
    sumpaid = 0;
  }
  const orderid = String(order?.orderid ?? order?.elig_hp_orderid ?? "").trim();
  const orderstatus = String(order?.orderstatus ?? order?.elig_hp_orderstatus ?? "")
    .trim()
    .toUpperCase();
  const paid = isTrueLike(order?.paid ?? order?.elig_hp_paid);
  const bookingNotConfirmed =
    !orderid ||
    orderid === "-1" ||
    !orderstatus ||
    orderstatus === "PENDING" ||
    !CONFIRMED_HOTEL_STATUSES.has(orderstatus);

  if ((paid || sumpaid > 0) && bookingNotConfirmed) {
    return { include: true, reason: HOTEL_PENDING_BOOKING };
  }
  if (!paid) {
    return { include: false, reason: "hotel_not_paid" };
  }
  if (!orderid || orderid === "-1") {
    return { include: false, reason: "gimmonix_order_not_booked" };
  }
  if (orderstatus && !CONFIRMED_HOTEL_STATUSES.has(orderstatus)) {
    return { include: false, reason: `hotel_orderstatus_${orderstatus.toLowerCase()}` };
  }
  return { include: true, reason: "hotel_paid_gimmonix_booked" };
}

function evaluateLdsRecentEligibility(order, { viewerIsAdmin = false } = {}) {
  if (isTrueLike(order?.cancelled)) {
    return { include: false, reason: "cancelled" };
  }
  const paid = isTrueLike(order?.paid);
  const requiresAdmin = isTrueLike(order?.requires_admin_attention);
  const ors =
    order?.order_response && typeof order.order_response === "object"
      ? order.order_response
      : {};
  const opStatus = String(ors?.opStatus?.status ?? "").trim().toUpperCase();
  const orderStatus = String(ors?.status ?? "").trim().toUpperCase();

  if (paid) {
    if (requiresAdmin && !viewerIsAdmin) {
      return { include: false, reason: "admin_attention_pending" };
    }
    if (requiresAdmin && viewerIsAdmin) {
      return { include: true, reason: "requires_admin_attention_paid" };
    }
    return { include: true, reason: "paid" };
  }

  const bookingRef = String(order?.booking_ref ?? "").trim();
  if (bookingRef) {
    return { include: true, reason: "booking_ref" };
  }

  if (orderStatus === "PENDING" || opStatus === "PENDING") {
    return { include: false, reason: "pending_unpaid" };
  }

  return { include: shouldIncludeLdsOrderInOrdersFeed(order), reason: "orders_feed_fallback" };
}

/**
 * Strict recent-orders feed (/getRecentOrdersAll) — mirrors BE recent_orders_eligibility.
 */
export function shouldShowInRecentOrders(order, options = {}) {
  const viewerIsAdmin =
    options.viewerIsAdmin ?? resolveHomeFeedViewerIsAdmin();
  const product = productType(order);
  if (product === "hotel") {
    return evaluateHotelRecentEligibility(order).include;
  }
  if (product === "package") {
    return evaluateLdsRecentEligibility(order, { viewerIsAdmin }).include;
  }
  return false;
}

/**
 * Global agents board — must include every row visible on recent feed (parity).
 */
export function shouldShowOrderOnGlobalBoard(order, options = {}) {
  const viewerIsAdmin = options.viewerIsAdmin ?? false;
  if (shouldShowInRecentOrders(order, { viewerIsAdmin })) {
    return true;
  }
  const product = productType(order);
  if (product === "hotel") {
    return evaluateHotelRecentEligibility(order).include;
  }
  if (product === "package") {
    return shouldIncludeLdsOrderInOrdersFeed(order);
  }
  return false;
}

export function buildOrderVisibilitySsotTrace(order, options = {}) {
  const viewerIsAdmin =
    options.viewerIsAdmin ?? resolveHomeFeedViewerIsAdmin();
  const product = productType(order);
  const ors =
    order?.order_response && typeof order.order_response === "object"
      ? order.order_response
      : {};

  const includedInRecent = shouldShowInRecentOrders(order, { viewerIsAdmin });
  let includedInGlobalBoard = shouldShowOrderOnGlobalBoard(order, { viewerIsAdmin });
  let recentReason = null;
  let globalReason = null;

  if (product === "hotel") {
    const ev = evaluateHotelRecentEligibility(order);
    recentReason = ev.reason;
    globalReason = ev.include ? ev.reason : ev.reason;
  } else if (product === "package") {
    const ev = evaluateLdsRecentEligibility(order, { viewerIsAdmin });
    recentReason = ev.reason;
    globalReason = shouldIncludeLdsOrderInOrdersFeed(order) ? "orders_feed" : ev.reason;
  }

  if (includedInRecent && !includedInGlobalBoard) {
    includedInGlobalBoard = true;
    globalReason = "recent_orders_parity_enforced";
  }

  let reasonIfExcluded = null;
  if (!includedInGlobalBoard) {
    reasonIfExcluded = globalReason || "not_eligible";
  } else if (!includedInRecent) {
    reasonIfExcluded = recentReason || "recent_excluded_global_included";
  }

  const businessStatus = isKnownBusinessStatus(order?.business_status)
    ? order.business_status
    : deriveLdsOrderBusinessStatus(order);

  return {
    order_id: order?.id ?? order?.order_id ?? null,
    booking_ref: order?.booking_ref ?? null,
    source_table:
      product === "hotel" ? "orders+hotelpackages" : product === "package" ? "lds_orders" : "unknown",
    product_type: product,
    viewer_is_admin: Boolean(viewerIsAdmin),
    includedInRecent,
    includedInGlobalBoard,
    reasonIfExcluded,
    recentReason,
    globalBoardReason: globalReason,
    paid: order?.paid ?? null,
    bank_transfer: order?.bank_transfer ?? null,
    cancelled: order?.cancelled ?? null,
    completion_state: order?.completion_state ?? null,
    order_response: {
      status: ors?.status ?? null,
      opStatus: ors?.opStatus?.status ?? null,
    },
    business_status: businessStatus,
    requires_admin_attention: order?.requires_admin_attention ?? null,
    post_payment_checkout_status: order?.post_payment_checkout_status ?? null,
  };
}

export function traceOrderVisibilitySsot(order, options = {}) {
  const payload = buildOrderVisibilitySsotTrace(order, options);
  if (import.meta.env?.DEV) {
    console.info(ORDER_VISIBILITY_SSOT_TRACE_TAG, payload);
  }
  return payload;
}

/**
 * Orders dashboard (agents): quote / inquiry rows hidden by default unless
 * includeFailedInquiry ("הצג הצעות מחיר / בירור").
 * Admin default skips this hide — see applyBusinessVisibilityToPackageLikeOrders.
 * Intentionally ignores global-board parity — opt-in checkbox controls agent visibility.
 */
export function isLdsPendingQuoteOrInquiryForAdminDashboard(order) {
  if (!order || typeof order !== "object") return false;
  if (isTrueLike(order.cancelled)) return false;

  // Operational rows — always visible in admin default view.
  if (isTrueLike(order.paid)) return false;
  if (isTrueLike(order.pending)) return false;

  const businessStatus = isKnownBusinessStatus(order.business_status)
    ? order.business_status
    : deriveLdsOrderBusinessStatus(order);
  if (
    businessStatus === LDS_ORDER_BUSINESS_STATUS.PAID ||
    businessStatus === LDS_ORDER_BUSINESS_STATUS.BOOKED
  ) {
    return false;
  }

  const entityType = String(order.entity_type ?? "").trim().toLowerCase();
  if (entityType === "proposal") return true;

  if (businessStatus === LDS_ORDER_BUSINESS_STATUS.PROPOSAL) return true;

  const agentStatus = String(order.agent_display_status ?? "").trim();
  if (agentStatus === "NEEDS_REVIEW" || agentStatus === "AWAITING_CARD_PAYMENT") {
    return true;
  }

  const message = String(order.agent_display_message ?? "").trim();
  if (message === "נדרש בירור") return true;
  if (businessStatus === LDS_ORDER_BUSINESS_STATUS.FAILED) return true;

  return false;
}

/**
 * LDS packages tab: do not hide rows that recent feed or global SSOT keeps visible.
 */
export function isLdsInquiryReviewOrderForDashboard(order, options = {}) {
  if (!order || typeof order !== "object") return false;
  if (shouldShowOrderOnGlobalBoard(order, options)) {
    return false;
  }
  if (order.agent_display_status === "NEEDS_REVIEW") return true;
  if (order.business_status === LDS_ORDER_BUSINESS_STATUS.FAILED) return true;
  const message = String(order.agent_display_message ?? "").trim();
  if (message === "נדרש בירור") return true;
  return deriveLdsOrderBusinessStatus(order) === LDS_ORDER_BUSINESS_STATUS.FAILED;
}
